// Mes réclamations (H7-03) : suivi, réponse de la collectivité, évaluation (note de 1 à 5 et
// commentaire) une fois la réponse donnée.
import { Bandeau, Bouton, EtatVide, Formulaire, LienBouton, ZoneTexte } from '@cc/ui';
import { MessageSquareWarning } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { CLES, useMesReclamations } from '../api';
import { EnTeteCitoyen, ExigeConnexion, TexteStatut } from '../commun';
import type { ReclamationUsager } from '../types';
import styles from '../citoyen.module.css';
import depot from '../depot/depot.module.css';

export const LIBELLES_ETAT_RECLAMATION: Record<string, { libelle: string; ton: 'info' | 'ok' | 'neutre' | 'attention' }> = {
  recue: { libelle: 'Reçue', ton: 'info' },
  en_instruction: { libelle: 'En cours d\'examen', ton: 'info' },
  repondue: { libelle: 'Réponse disponible', ton: 'ok' },
  cloturee: { libelle: 'Clôturée', ton: 'neutre' },
};

function Evaluation({ r }: { r: ReclamationUsager }) {
  const [note, setNote] = useState<number | null>(null);
  const [commentaire, setCommentaire] = useState('');
  const evaluation = useAction({
    executer: () => requete(`/usager/reclamations/${r.id}/evaluer`, { methode: 'POST', corps: { note, ...(commentaire.trim() ? { commentaire: commentaire.trim() } : {}) } }),
    succes: 'Merci, votre évaluation est enregistrée.',
    invalider: [[...CLES.reclamations]],
  });
  return (
    <Formulaire
      libelle={`Évaluer la réponse à ${r.numero}`}
      onSoumettre={() => {
        if (note) evaluation.lancer(undefined);
      }}
      actions={
        <Bouton type="submit" variante="secondaire" petit chargement={evaluation.enCours} disabled={!note}>
          Envoyer l&apos;évaluation
        </Bouton>
      }
    >
      {evaluation.messageErreur ? <Bandeau type="erreur">{evaluation.messageErreur}</Bandeau> : null}
      <fieldset className={depot.groupe}>
        <legend>Votre satisfaction, de 1 (insatisfait) à 5 (très satisfait)</legend>
        <div className={styles.filtres} role="radiogroup">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" role="radio" aria-checked={note === n} className={styles.filtre} onClick={() => setNote(n)} aria-label={`Note ${n} sur 5`}>
              {n}
            </button>
          ))}
        </div>
      </fieldset>
      <ZoneTexte libelle="Commentaire" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} facultatif rows={2} maxLength={2000} />
    </Formulaire>
  );
}

function CarteReclamation({ r }: { r: ReclamationUsager }) {
  const etat = LIBELLES_ETAT_RECLAMATION[r.etat] ?? { libelle: r.etat, ton: 'neutre' as const };
  return (
    <li className={styles.carte} aria-label={`Réclamation ${r.numero}`}>
      <div className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>
          <span className={styles.mono}>{r.numero}</span> · {r.categorie_libelle}
        </span>
        <span className={styles.ligneSous}>
          {r.collectivite.nom} · déposée le {date(r.cree_le)}
          {r.echeance && (r.etat === 'recue' || r.etat === 'en_instruction') ? ` · réponse attendue le ${date(r.echeance)}` : ''}
          {r.sensible ? ' · sensible, accès restreint aux personnes habilitées' : ''}
        </span>
        <TexteStatut libelle={etat.libelle} ton={etat.ton} />
      </div>
      <p>{r.description}</p>
      {r.reponse ? (
        <section className={styles.bloc} aria-label="Réponse de la collectivité">
          <span className={styles.blocEtiquette}>Réponse de la collectivité{r.repondue_le ? `, le ${date(r.repondue_le)}` : ''}</span>
          <p className={styles.blocTexte}>{r.reponse}</p>
        </section>
      ) : null}
      {r.evaluation ? (
        <p className={styles.aide}>
          Votre évaluation : {r.evaluation.note} sur 5{r.evaluation.commentaire ? ` · « ${r.evaluation.commentaire} »` : ''}
        </p>
      ) : r.etat === 'repondue' || r.etat === 'cloturee' ? (
        <Evaluation r={r} />
      ) : null}
    </li>
  );
}

export function PageReclamations() {
  const reclamations = useMesReclamations();
  return (
    <>
      <EnTeteCitoyen
        titre="Réclamations"
        sousTitre="Signalez un problème d'accueil, de délai ou de service rendu. Réponse attendue sous quinze jours ouvrés."
        actions={
          <LienBouton href="/citoyen/reclamations/nouvelle" variante="principal">
            Déposer une réclamation
          </LienBouton>
        }
      />
      <ExigeConnexion titre="Connectez-vous pour suivre vos réclamations">
        <EtatRequete chargement={reclamations.isPending} erreur={reclamations.error} onReessayer={() => void reclamations.refetch()} fonction="Vos réclamations" lignes={4}>
          {(reclamations.data ?? []).length === 0 ? (
            <EtatVide titre="Aucune réclamation" icone={MessageSquareWarning}>
              <p>Vos réclamations et les réponses de la collectivité apparaîtront ici.</p>
            </EtatVide>
          ) : (
            <ul className={styles.pile} aria-label="Réclamations">
              {(reclamations.data ?? []).map((r) => (
                <CarteReclamation key={r.id} r={r} />
              ))}
            </ul>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
